import { Badge, Button } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { OrderLineVideoLabelEditor } from "@/features/marketplace/components/OrderLineVideoLabelEditor";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { videoReviewBadgeVariant } from "@/features/marketplace/lib/line-status-display";
import { orderLineVideoDisplayName } from "@/features/marketplace/lib/order-line-videos";
import { useUpdateOrderScriptVideoLabel } from "@/lib/queries/marketplace.queries";
import {
  canAddPartnerVideo,
  canChangePartnerVideos,
  MAX_VIDEOS_PER_LINE,
  partnerLineVideos,
  partnerVideoSelectionKey,
} from "@/features/marketplace/partner/lib/partner-videos";
import type {
  OrderLineVideo,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

export function PartnerLineVideos({
  script,
  title,
  selectedKey,
  onReplace,
  onAddInstead,
}: {
  script: OrderScriptRow;
  title: string;
  selectedKey: string | null;
  onReplace: (video: OrderLineVideo) => void;
  onAddInstead: () => void;
}) {
  const { t } = useTranslation();
  const updateLabel = useUpdateOrderScriptVideoLabel();
  const videos = partnerLineVideos(script);
  if (videos.length === 0) return null;

  const canChange = canChangePartnerVideos(script.fulfillmentStatus);
  const canAdd = canAddPartnerVideo(script);

  return (
    <div className="flex flex-col gap-2">
      <ul
        aria-label={t("marketplace.partner.videos.listLabel", "Videos")}
        className="flex flex-col gap-2"
      >
        {videos.map((video) => {
          const label = orderLineVideoDisplayName(video, t);
          const key = partnerVideoSelectionKey(video);
          const selected = selectedKey === key;
          return (
            <li
              key={key}
              className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2"
            >
              <div className="flex min-h-11 flex-wrap items-center justify-between gap-2">
                <OrderLineVideoLabelEditor
                  video={video}
                  editable={canChange && video.id !== null}
                  isPending={updateLabel.isPending}
                  onSave={(nextLabel) => {
                    if (!video.id) return;
                    updateLabel.mutate({
                      orderId: script.orderId,
                      orderScriptId: script.id,
                      deliverableId: video.id,
                      label: nextLabel,
                    });
                  }}
                />
                <Badge variant={videoReviewBadgeVariant(video.reviewStatus)} dot>
                  {t(
                    video.reviewStatus === "REVISION_REQUESTED"
                      ? "marketplace.partner.videos.status.needsRevision"
                      : "marketplace.partner.videos.status.inReview",
                    video.reviewStatus === "REVISION_REQUESTED"
                      ? "Needs revision"
                      : "In review",
                  )}
                </Badge>
              </div>
              {video.revisionNote?.trim() ? (
                <p className="text-[11px] leading-4 text-warning-subtle-foreground">
                  {video.revisionNote}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                {video.scriptReviewId || video.preflightUrl ? (
                  <ScriptReviewAction
                    scriptReviewId={video.scriptReviewId}
                    preflightUrl={video.preflightUrl}
                    label={t("marketplace.partner.actions.view", "View")}
                    ariaLabel={t("marketplace.partner.videos.viewAria", {
                      label,
                      title,
                      defaultValue: "View review for {{label}} on {{title}}",
                    })}
                    variant="outline"
                    size="sm"
                    compact
                  />
                ) : null}
                {canChange ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={selected ? "default" : "outline"}
                    aria-pressed={selected}
                    onClick={() => onReplace(video)}
                  >
                    {t("marketplace.partner.videos.replace", "Replace")}
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {selectedKey && canAdd ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={onAddInstead}
        >
          {t("marketplace.partner.videos.addInstead", "Add a video instead")}
        </Button>
      ) : null}
      {!canAdd && canChange ? (
        <p className="text-xs text-muted-foreground">
          {t("marketplace.partner.videos.capReached", {
            count: MAX_VIDEOS_PER_LINE,
            defaultValue_one: "This script already has {{count}} video.",
            defaultValue_other: "This script already has {{count}} videos.",
          })}
        </p>
      ) : null}
    </div>
  );
}
